import React, { useState, useEffect } from 'react';
import {
    Card, DatePicker, Select, Button, Row, Col, Table, Typography, Empty, message, Space
} from 'antd';
import {
    SearchOutlined, PieChartOutlined, GlobalOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { reportService, pbxReportCatalogService } from '../../services/api';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

// Color palette for dynamic segments
const PALETTE = [
    '#52c41a', '#1890ff', '#faad14', '#f5222d', '#722ed1', '#13c2c2', 
    '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb', '#fa541c', '#13c2c2'
];

const SimplePieChart = ({ data }) => {
    const total = data.reduce((acc, cur) => acc + cur.value, 0);
    if (total === 0) return <Empty description="Sin datos" image={Empty.PRESENTED_IMAGE_SIMPLE} />;

    let currentAngle = 0;
    const segments = data.map((item, index) => {
        const color = item.color || PALETTE[index % PALETTE.length];
        const angle = (item.value / total) * 360;
        const start = currentAngle;
        currentAngle += angle;
        return `${color} ${start}deg ${currentAngle}deg`;
    });

    const background = `conic-gradient(${segments.join(', ')})`;

    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
            <div style={{
                width: 250, height: 250, borderRadius: '50%',
                background: background,
                position: 'relative',
                border: '1px solid #f0f0f0'
            }} />
            <div style={{ marginTop: 20, display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', maxHeight: '150px', overflowY: 'auto', padding: '0 10px' }}>
                {data.map((d, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                        <div style={{ width: 10, height: 10, backgroundColor: d.color || PALETTE[i % PALETTE.length], marginRight: 5, borderRadius: 2 }} />
                        <Text>{d.name}: {d.value} ({((d.value/total)*100).toFixed(1)}%)</Text>
                    </div>
                ))}
            </div>
        </div>
    );
};

const GeoCoverageReport = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);

    // Filters state
    const [filters, setFilters] = useState({
        date_range: [dayjs().startOf('month'), dayjs().endOf('month')],
        campaign_id: undefined,
    });

    useEffect(() => {
        const fetchCampaigns = async () => {
            try {
                const res = await pbxReportCatalogService.getCampaigns();
                if (res.status === 'success') setCampaigns(res.data);
            } catch (error) {
                console.error("Error fetching campaigns", error);
            }
        };
        fetchCampaigns();
    }, []);

    const fetchData = async () => {
        if (!filters.campaign_id) {
            message.warning("Debe seleccionar una campaña");
            return;
        }

        setLoading(true);
        setHasSearched(true);
        try {
            const params = {
                campaign_id: filters.campaign_id,
                date_range: filters.date_range ? filters.date_range.map(d => d.format('YYYY-MM-DD')).join(',') : null
            };
            const response = await reportService.getGeoCoverageReport(params);
            if (response.status === 'success') {
                setData(response.data);
            } else {
                setData([]);
            }
        } catch (error) {
            console.error(error);
            message.error("Error al cargar reporte de cobertura");
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Ubicación (Departamento)',
            dataIndex: 'location',
            key: 'location',
            render: (text) => <b>{text}</b>
        },
        {
            title: 'Cantidad de Llamadas',
            dataIndex: 'call_count',
            key: 'call_count',
            align: 'right',
            sorter: (a, b) => a.call_count - b.call_count,
            render: (val) => <Text strong>{val}</Text>
        },
        {
            title: 'Distribución',
            key: 'distribution',
            align: 'right',
            render: (_, record) => {
                const total = data.reduce((acc, cur) => acc + parseInt(cur.call_count), 0);
                const percent = (parseInt(record.call_count) / total * 100).toFixed(1);
                return `${percent}%`;
            }
        }
    ];

    const chartData = data.map((item, index) => ({
        name: item.location,
        value: parseInt(item.call_count),
        color: item.location === 'No determinada' ? '#bfbfbf' : PALETTE[index % PALETTE.length]
    }));

    return (
        <div className="fade-in">
            <Card bordered={false} style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <Space wrap>
                    <Select
                        placeholder="Seleccione Campaña"
                        value={filters.campaign_id}
                        onChange={(val) => setFilters({ ...filters, campaign_id: val })}
                        style={{ width: 250 }}
                        optionFilterProp="children"
                        showSearch
                    >
                        {campaigns.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                    </Select>
                    <RangePicker
                        value={filters.date_range}
                        onChange={(val) => setFilters({ ...filters, date_range: val })}
                        placeholder={['Inicio', 'Fin']}
                    />
                    <Button type="primary" icon={<SearchOutlined />} onClick={fetchData} loading={loading}>
                        Mostrar datos
                    </Button>
                </Space>
            </Card>

            {!hasSearched || data.length === 0 ? (
                <Empty description={hasSearched ? "No se encontraron datos" : "Seleccione una campaña para ver la cobertura geográfica"} />
            ) : (
                <Row gutter={[24, 24]}>
                    <Col xs={24} lg={14}>
                        <Card title={<Space><GlobalOutlined /> Listado por Ubicación</Space>}>
                            <Table
                                columns={columns}
                                dataSource={data.map((item, i) => ({ ...item, key: i }))}
                                pagination={false}
                                size="middle"
                                bordered
                            />
                        </Card>
                    </Col>
                    <Col xs={24} lg={10}>
                        <Card title={<Space><PieChartOutlined /> Distribución Geográfica</Space>} style={{ height: '100%' }}>
                            <SimplePieChart data={chartData} />
                        </Card>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default GeoCoverageReport;
